import React, { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Placement } from 'popper.js';
import { Callout } from 'components/layout/Callout';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { ButtonPanel } from 'components/Dialogs/ButtonPanel';

export interface IPropertyGridLayerProps {
  onClose: () => void;
  sourceRef: React.MutableRefObject<HTMLElement | any>;
  placement?: Placement;
  getEditingContextId?: () => Promise<string>;
  mappingContext?: MappingContext;
}

export const PropertyGridLayer: FC<IPropertyGridLayerProps> = (props: IPropertyGridLayerProps) => {
  const propertyGrid = useRef<{ submitAndDispose: () => Promise<boolean> } | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingContextId, setEditingContextId] = useState('');

  const { getEditingContextId, mappingContext } = props;
  useEffect(() => {
    if (!editingContextId.length && getEditingContextId) {
      setLoading(true);
      getEditingContextId().then(editingContext => {
        setEditingContextId(editingContext);
        setLoading(false);
      });
    }
  }, [editingContextId, getEditingContextId]);

  const { onClose } = props;
  const handleDone = useCallback(async () => {
    await propertyGrid.current?.submitAndDispose();
    onClose();
  }, [onClose]);

  const renderPropertyGrid = useCallback(
    async (element: HTMLDivElement) => {
      if (!DEV_SERVER) {
        propertyGrid.current = $DP.PropertyGrid.createGridFromModel(element, {
          editingContextId,
          skipValidationDialog: true,
          mappingContext,
        });
      }
    },
    [editingContextId, mappingContext]
  );

  const view = useMemo(() => {
    if (loading || !editingContextId?.length)
      return (
        <div className="flex-centering full-size">
          <span className="three-dots">Loading</span>
        </div>
      );

    return (
      <>
        <div ref={renderPropertyGrid}></div>
        <ButtonPanel
          className="dp-property-grid-layer__footer"
          actions={[{
            name: "Done",
            onClick: handleDone,
          }]}
          disablePadding />
      </>
    );
  }, [loading, editingContextId.length, renderPropertyGrid, handleDone]);

  return (
    <Callout
      className="dp-property-grid-layer"
      isOpen={true}
      onClose={handleDone}
      sourceRef={props.sourceRef}
      placement={!props.placement ? props.placement : 'bottom-end'}
      showArrow={true}
      flipBehavior={['bottom', 'left']}
    >
      <div className="dp-property-grid-layer__editor">{view}</div>
    </Callout>
  );
};
